import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProductAnalysisSkuSummary } from '@ozon-ai/analysis/contracts';
import type { ProductCatalogItem } from '@ozon-ai/ozon/contracts';
import { api } from '../../api/client';
import { searchCatalog } from '../../components/offerCatalogSearch';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  formatAnalysisDate,
  isSuccessfulRunStatus,
} from '../../components/product-analysis/productAnalysisLabels';
import { ContentCard } from '../../ui/ContentCard';
import { ErrorText, HintText, PageShell } from '../../ui/PageShell';

export function SkuAnalysisListPage() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [summaries, setSummaries] = useState<ProductAnalysisSkuSummary[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summaryByOfferId = useMemo(
    () => new Map(summaries.map((item) => [item.offerId, item])),
    [summaries]
  );

  const items = useMemo(() => searchCatalog(catalog, query), [catalog, query]);

  const load = useCallback(async (refresh = false) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const [catalogItems, skuSummaries] = await Promise.all([
        api.get<ProductCatalogItem[]>(`/products/catalog${refresh ? '?refresh=true' : ''}`),
        api.get<ProductAnalysisSkuSummary[]>('/product-analyses/skus'),
      ]);
      setCatalog(catalogItems);
      setSummaries(skuSummaries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить список SKU');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <PageShell
      title="Анализ SKU"
      action={
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/analyses')}>
          Назад
        </Button>
      }
    >
      <HintText>Выберите товар из каталога Ozon для просмотра истории анализов или запуска нового.</HintText>

      <ContentCard>
        <div className="flex flex-col gap-3">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск по артикулу, названию или SKU"
            autoComplete="off"
          />
          <Button type="button" disabled={refreshing} onClick={() => void load(true)}>
            {refreshing ? 'Обновление…' : 'Обновить каталог'}
          </Button>
        </div>
      </ContentCard>

      {error && <ErrorText>{error}</ErrorText>}

      {catalog.length === 0 && !error && (
        <ContentCard>
          <p className="mb-2 text-sm font-semibold">Каталог пуст</p>
          <p className="text-xs text-muted-foreground">
            Подключите Ozon в настройках и обновите каталог.
          </p>
        </ContentCard>
      )}

      {items.map((item) => {
        const summary = summaryByOfferId.get(item.offerId);
        const lastSuccessfulAt =
          summary && isSuccessfulRunStatus(summary.lastStatus)
            ? summary.lastFinishedAt
            : undefined;

        return (
          <ContentCard
            key={item.offerId}
            onClick={() => navigate(`/analyses/sku/${encodeURIComponent(item.offerId)}`)}
          >
            <div className="flex items-start gap-3">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  width={56}
                  height={56}
                  className="size-14 shrink-0 rounded-lg object-cover"
                  alt=""
                />
              ) : (
                <div className="flex size-14 shrink-0 items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
                  нет фото
                </div>
              )}
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <p className="text-sm font-semibold">{item.name}</p>
                <p className="text-xs text-muted-foreground">{item.offerId}</p>
                {item.ozonSku && (
                  <p className="text-xs text-muted-foreground">SKU Ozon: {item.ozonSku}</p>
                )}
                {summary && summary.runCount > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {lastSuccessfulAt
                      ? `Последний анализ: ${formatAnalysisDate(lastSuccessfulAt)}`
                      : `Запусков: ${summary.runCount}`}
                    {summary.runCount > 1 ? ` · всего ${summary.runCount}` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Ещё не анализировали</p>
                )}
              </div>
            </div>
          </ContentCard>
        );
      })}
    </PageShell>
  );
}
